import type { OpenReplayConfig } from '@ztechapps/common.contract'
import { tracker } from '@openreplay/tracker'
import React, { useEffect, useRef, useMemo, useCallback } from 'react'
import { hasWindow, getWindow } from '@ztechapps/common.utils'
import { OpenReplayContext, type OpenReplayContextType } from './OpenReplayContext'
import { useTelemetry } from '../hooks/useTelemetry'

// Only intentional user actions — excludes mousemove/scroll which fire from programmatic events, layout shifts, etc.
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart'] as const

/** Throttle interval for the activity handler to avoid excessive calls */
const ACTIVITY_THROTTLE_MS = 2000

/** Module-level guard so `tracker.configure()` is only called once across renders and StrictMode double-mounts */
let configured = false

export type OpenReplayProviderProps = {
  config: OpenReplayConfig
  children: React.ReactNode
}

export const OpenReplayProvider = ({ config, children }: OpenReplayProviderProps) => {
  const pendingUserID = useRef<string | null>(null)
  const pendingMetadata = useRef<Record<string, string>>({})
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activityThrottleRef = useRef(false)
  const telemetry = useTelemetry()

  const applyPending = useCallback(() => {
    if (pendingUserID.current) {
      tracker.setUserID(pendingUserID.current)
    }
    tracker.setMetadata('environment', config?.environment ?? 'unknown environment')

    Object.entries(pendingMetadata.current).forEach(([key, value]) => {
      tracker.setMetadata(key, value)
    })
  }, [config.environment])

  const bridgeOtelSessionId = useCallback(() => {
    try {
      const sessionID = tracker.getSessionID()
      if (sessionID && telemetry) {
        telemetry.setGlobalAttribute('openreplay.session.id', sessionID)
      }
    } catch {
      // Silently ignore — telemetry bridge is non-critical
    }
  }, [telemetry])

  useEffect(() => {
    if (!hasWindow()) return

    if (!config.enabled) {
      console.info('[OpenReplay] Disabled')
      return
    }

    console.info(`[OpenReplay] Enabled (environment: ${config.environment})`)

    // Configure the singleton once — idempotent guard prevents double-configure in StrictMode
    if (!configured) {
      tracker.configure({
        projectKey: config.projectKey,
        // InputMode.Obscured — masks all input values by default
        defaultInputMode: 1,
        // Inline CSS into recordings — required when OpenReplay server can't reach app assets (localhost/private network)
        ...(config.inlineCss !== undefined ? { inlineCss: config.inlineCss } : {}),
        // Self-hosted ingest endpoint — must be absolute URL for the tracker's web worker
        ...(config.ingestPoint ? { ingestPoint: `${getWindow().location.origin}${config.ingestPoint}` } : {}),
        // Allow insecure connection on localhost or self-signed certs — per OpenReplay docs
        __DISABLE_SECURE_MODE: true,
      })
      configured = true
    }

    const useColdStart = (config.inactivityTimeout ?? 0) > 0

    // ----- Always-on mode (backwards compatible) -----
    if (!useColdStart) {
      const initTracker = async () => {
        try {
          const result = await tracker.start()
          console.info('[OpenReplay] Start result:', result)
          if (result?.success) {
            console.info('[OpenReplay] Session started. ID:', tracker.getSessionID(), 'URL:', tracker.getSessionURL())
            applyPending()
            bridgeOtelSessionId()
          }
        } catch {
          // Silently ignore — tracker init is non-critical (ad blockers may block it)
        }
      }

      initTracker()

      return
    }

    // ----- Activity-based (coldStart) mode -----
    const timeout = config.inactivityTimeout ?? 0
    let destroyed = false

    const clearInactivityTimer = () => {
      if (inactivityTimerRef.current !== null) {
        clearTimeout(inactivityTimerRef.current)
        inactivityTimerRef.current = null
      }
    }

    const enterColdStart = async () => {
      try {
        await tracker.coldStart()
        console.info('[OpenReplay] Cold start — buffering silently')
      } catch {
        // Silently ignore — cold start is non-critical
      }
    }

    const onInactivityTimeout = async () => {
      if (destroyed) return

      try {
        if (tracker.isActive()) {
          console.info('[OpenReplay] Inactivity timeout — stopping session')
          tracker.stop()
          await enterColdStart()
        }
      } catch {
        // Silently ignore — stop/coldStart is non-critical
      }
    }

    const resetInactivityTimer = () => {
      clearInactivityTimer()
      inactivityTimerRef.current = setTimeout(() => {
        onInactivityTimeout()
      }, timeout)
    }

    const promoteToSession = async () => {
      try {
        const result = await tracker.start()
        console.info('[OpenReplay] Promoted cold buffer to session. Result:', result)
        if (result?.success) {
          console.info('[OpenReplay] Session started. ID:', tracker.getSessionID(), 'URL:', tracker.getSessionURL())
          applyPending()
          bridgeOtelSessionId()
        }
      } catch {
        // Silently ignore — start is non-critical
      }
    }

    const onActivity = () => {
      if (destroyed) return

      // Throttle: skip if we are already processing within the throttle window
      if (activityThrottleRef.current) return
      activityThrottleRef.current = true
      setTimeout(() => {
        activityThrottleRef.current = false
      }, ACTIVITY_THROTTLE_MS)

      // Always reset the inactivity timer on activity
      resetInactivityTimer()

      // If not currently recording, promote the cold buffer to a real session
      if (!tracker.isActive()) {
        promoteToSession()
      }
    }

    const initColdStart = async () => {
      try {
        await enterColdStart()

        // Attach activity listeners on the document
        const doc = getWindow().document
        ACTIVITY_EVENTS.forEach((event) => {
          doc.addEventListener(event, onActivity, { passive: true, capture: true })
        })
      } catch {
        // Silently ignore — tracker init is non-critical (ad blockers may block it)
      }
    }

    initColdStart()

    return () => {
      destroyed = true
      clearInactivityTimer()
      activityThrottleRef.current = false

      // Remove activity listeners
      if (hasWindow()) {
        const doc = getWindow().document
        ACTIVITY_EVENTS.forEach((event) => {
          doc.removeEventListener(event, onActivity, { capture: true })
        })
      }
    }
  }, [
    config.enabled,
    config.projectKey,
    config.ingestPoint,
    config.inactivityTimeout,
    config.environment,
    config.inlineCss,
    telemetry,
    applyPending,
    bridgeOtelSessionId,
  ])

  const value = useMemo<OpenReplayContextType>(
    () => ({
      setUserID: (id: string) => {
        pendingUserID.current = id
        tracker.setUserID(id)
      },
      setMetadata: (key: string, val: string) => {
        pendingMetadata.current[key] = val
        tracker.setMetadata(key, val)
      },
      getSessionID: () => {
        return tracker.getSessionID() ?? null
      },
    }),
    []
  )

  return <OpenReplayContext.Provider value={value}>{children}</OpenReplayContext.Provider>
}
